import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PageBackground } from "@/components/PageBackground";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | failed | cancelled
  const polled = useRef(false);

  useEffect(() => {
    if (polled.current) return;
    polled.current = true;
    if (params.get("cancelled")) { setState("cancelled"); return; }
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }
    const isSub = params.get("sub") === "1";
    const statusPath = isSub ? `/subscription/status/${sessionId}` : `/payments/status/${sessionId}`;

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(statusPath);
        if (data.payment_status === "paid") {
          await refresh();
          setState("success");
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
      } catch (e) { console.debug("payment poll retry", e?.message); }
      if (attempts >= 6) { setState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params, refresh]);

  const dash = user?.role === "landlord" ? "/landlord" : "/tenant";

  return (
    <div className="min-h-screen grid place-items-center px-6">
      <PageBackground variant={3} />
      <div className="bg-white border border-border rounded-sm p-10 max-w-md w-full text-center">
        {state === "checking" && (<><Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" /><h1 className="font-display text-2xl font-semibold">Confirming payment…</h1></>)}
        {state === "success" && (<><CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" /><h1 className="font-display text-2xl font-semibold mb-2">Payment successful</h1><p className="text-muted-foreground mb-6">Your account has been updated.</p><Button data-testid="payment-continue" onClick={() => navigate(dash)}>Continue</Button></>)}
        {state === "cancelled" && (<><XCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" /><h1 className="font-display text-2xl font-semibold mb-2">Payment cancelled</h1><Button variant="outline" onClick={() => navigate(dash)}>Back to dashboard</Button></>)}
        {state === "failed" && (<><XCircle className="w-12 h-12 text-destructive mx-auto mb-4" /><h1 className="font-display text-2xl font-semibold mb-2">Couldn't confirm payment</h1><p className="text-muted-foreground mb-6">If you were charged, it will reflect shortly.</p><Button variant="outline" onClick={() => navigate(dash)}>Back to dashboard</Button></>)}
      </div>
    </div>
  );
}
